import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import {
  AlertTriangle,
  Edit,
  Loader2,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import {
  createUserLicenseTypeAdminUserLicenseTypesPost as createUserLicenseType,
  deleteUserLicenseTypeAdminUserLicenseTypesTypeIdDelete as retireUserLicenseType,
  listUserLicenseTypesAdminUserLicenseTypesGet as listUserLicenseTypes,
  updateUserLicenseTypeAdminUserLicenseTypesTypeIdPut as updateUserLicenseType,
} from '../../services/api/sdk.gen';
import type { UserLicenseTypeResponse } from '../../services/api/types.gen';
import TablePaginationFooter from '../../components/TablePaginationFooter';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

const typeFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(100, 'Name must be 100 characters or fewer'),
});

type TypeFormData = z.infer<typeof typeFormSchema>;

const TYPE_FORM_DEFAULTS: TypeFormData = { name: '' };

const PAGE_SIZE_OPTIONS = [10, 25, 50];

/**
 * Admin screen for the license types an agency can record against its staff.
 *
 * Two kinds of row share one table. A row Roger ships (`is_default`) is offered
 * to every agency and cannot be renamed or retired here, which is why those rows
 * carry no controls: the list is a starting point an agency adds to, not one it
 * curates. A row the agency created is fully theirs.
 *
 * Retiring is not deleting. The licenses already recorded against a type keep
 * resolving its name, so the row stays and simply stops being offered.
 */
const UserLicenseTypesSection: React.FC = () => {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [types, setTypes] = useState<UserLicenseTypeResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  // Monotonic load generation; only the newest load may write to the table.
  const requestIdRef = useRef(0);
  const [search, setSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [pendingRetire, setPendingRetire] =
    useState<UserLicenseTypeResponse | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<TypeFormData>({
    resolver: zodResolver(typeFormSchema),
    defaultValues: TYPE_FORM_DEFAULTS,
    mode: 'onSubmit',
  });

  const loadTypes = useCallback(async () => {
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    const isCurrent = () => requestIdRef.current === requestId;
    setIsLoading(true);
    try {
      const result = await listUserLicenseTypes({
        // Retired types have to be listed here: bringing one back is only
        // possible if it can be seen.
        query: { include_inactive: true },
        throwOnError: true,
      });
      if (isCurrent()) {
        setTypes(result.data ?? []);
        setLoadFailed(false);
      }
    } catch (error) {
      console.error('Failed to load license types', error);
      if (isCurrent()) {
        setLoadFailed(true);
        showError('Failed to load license types');
      }
    } finally {
      if (isCurrent()) setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    void loadTypes();
  }, [loadTypes]);

  const filteredTypes = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return types;
    return types.filter(type => type.name.toLowerCase().includes(query));
  }, [types, search]);

  useEffect(() => {
    setPageIndex(0);
  }, [search, pageSize]);

  const pageCount = Math.max(1, Math.ceil(filteredTypes.length / pageSize));
  const safePageIndex = Math.min(pageIndex, pageCount - 1);
  const visibleTypes = filteredTypes.slice(
    safePageIndex * pageSize,
    safePageIndex * pageSize + pageSize
  );

  const openCreateModal = useCallback(() => {
    setEditingId(null);
    setSubmitError(null);
    reset(TYPE_FORM_DEFAULTS);
    setIsModalOpen(true);
  }, [reset]);

  const openEditModal = useCallback(
    (type: UserLicenseTypeResponse) => {
      setEditingId(type.id);
      setSubmitError(null);
      reset({ name: type.name });
      setIsModalOpen(true);
    },
    [reset]
  );

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingId(null);
    setSubmitError(null);
  }, []);

  const onSubmit = useCallback(
    async (data: TypeFormData) => {
      setSubmitError(null);
      try {
        if (editingId) {
          await updateUserLicenseType({
            path: { type_id: editingId },
            body: { name: data.name },
            throwOnError: true,
          });
          showSuccess('License type updated');
        } else {
          await createUserLicenseType({
            body: { name: data.name },
            throwOnError: true,
          });
          showSuccess('License type added');
        }
        closeModal();
        await loadTypes();
      } catch (error) {
        console.error('Failed to save license type', error);
        // The duplicate-name refusal names the problem, so it is worth showing
        // in the form rather than replacing with a generic retry.
        setSubmitError(
          getBusinessErrorMessage(error) ??
            'Failed to save this license type. Please try again.'
        );
      }
    },
    [editingId, closeModal, loadTypes, showSuccess]
  );

  const confirmRetire = useCallback(async () => {
    if (!pendingRetire) return;
    setBusyId(pendingRetire.id);
    try {
      await retireUserLicenseType({
        path: { type_id: pendingRetire.id },
        throwOnError: true,
      });
      showSuccess(`${pendingRetire.name} retired`);
      setPendingRetire(null);
      await loadTypes();
    } catch (error) {
      console.error('Failed to retire license type', error);
      showError(
        getBusinessErrorMessage(error) ??
          'Failed to retire this license type. Please try again.'
      );
    } finally {
      setBusyId(null);
    }
  }, [pendingRetire, loadTypes, showSuccess, showError]);

  const reactivate = useCallback(
    async (type: UserLicenseTypeResponse) => {
      setBusyId(type.id);
      try {
        await updateUserLicenseType({
          path: { type_id: type.id },
          body: { is_active: true },
          throwOnError: true,
        });
        showSuccess(`${type.name} restored`);
        await loadTypes();
      } catch (error) {
        console.error('Failed to restore license type', error);
        showError(
          getBusinessErrorMessage(error) ??
            'Failed to restore this license type. Please try again.'
        );
      } finally {
        setBusyId(null);
      }
    },
    [loadTypes, showSuccess, showError]
  );

  return (
    <div className='space-y-6'>
      <div className='space-y-1.5'>
        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
          <div>
            <p className='text-sm text-muted-foreground'>
              The licenses and certifications you can record against a member of
              staff. Roger&apos;s defaults are always available; add your own
              for anything else you track.
            </p>
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Input
              inputSize='sm'
              value={search}
              onChange={event => setSearch(event.target.value)}
              placeholder='Search types'
              aria-label='Search license types'
              className='w-full md:w-56'
            />
            <Button
              type='button'
              variant='outline'
              size='sm'
              onClick={() => void loadTypes()}
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className='mr-1 h-3.5 w-3.5 animate-spin' />
              ) : (
                <RefreshCw className='mr-1 h-3.5 w-3.5' />
              )}
              Refresh
            </Button>
            <Button type='button' size='sm' onClick={openCreateModal}>
              <Plus className='mr-1 h-3.5 w-3.5' />
              Add type
            </Button>
          </div>
        </div>
      </div>

      <div className='space-y-3'>
        {loadFailed && (
          <Alert className='border-red-200 bg-red-50'>
            <AlertDescription className='flex items-center gap-2 text-red-800'>
              <AlertTriangle className='h-4 w-4 text-red-600' />
              Failed to load license types. Use Refresh to try again.
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && filteredTypes.length === 0 && (
          <p className='text-sm text-muted-foreground'>
            {search.trim()
              ? 'No license types match that search.'
              : 'No license types yet.'}
          </p>
        )}

        {filteredTypes.length > 0 && (
          <div className='overflow-x-auto rounded-md border'>
            <Table className='min-w-[560px]'>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className='text-right'>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleTypes.map(type => (
                  <TableRow key={type.id}>
                    <TableCell className='font-medium'>{type.name}</TableCell>
                    <TableCell>
                      <Badge
                        variant='outline'
                        className='whitespace-nowrap text-xs'
                      >
                        {type.is_default ? 'Roger default' : 'Added by you'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {type.is_active ? (
                        <span className='text-sm text-muted-foreground'>
                          Active
                        </span>
                      ) : (
                        <Badge
                          variant='outline'
                          className='whitespace-nowrap border-amber-300 bg-amber-50 text-xs text-amber-800'
                        >
                          Retired
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className='text-right'>
                      {type.is_default ? (
                        // Roger owns these rows. Rendering disabled controls
                        // would invite the click; saying why costs one line.
                        <span className='text-xs text-muted-foreground'>
                          Managed by Roger
                        </span>
                      ) : (
                        <div className='flex flex-wrap justify-end gap-1'>
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => openEditModal(type)}
                            disabled={busyId === type.id}
                          >
                            <Edit className='mr-1 h-3.5 w-3.5' />
                            Edit
                          </Button>
                          {type.is_active ? (
                            <Button
                              type='button'
                              variant='ghost'
                              size='sm'
                              aria-label={`Retire ${type.name}`}
                              onClick={() => setPendingRetire(type)}
                              disabled={busyId === type.id}
                            >
                              <Trash2 className='h-3.5 w-3.5 text-destructive' />
                            </Button>
                          ) : (
                            <Button
                              type='button'
                              variant='ghost'
                              size='sm'
                              aria-label={`Restore ${type.name}`}
                              onClick={() => void reactivate(type)}
                              disabled={busyId === type.id}
                            >
                              {busyId === type.id ? (
                                <Loader2 className='h-3.5 w-3.5 animate-spin' />
                              ) : (
                                <RotateCcw className='h-3.5 w-3.5' />
                              )}
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        {filteredTypes.length > 0 && (
          <TablePaginationFooter
            pageIndex={safePageIndex}
            pageCount={pageCount}
            totalCount={filteredTypes.length}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            canPreviousPage={safePageIndex > 0}
            canNextPage={safePageIndex < pageCount - 1}
            onPageSizeChange={setPageSize}
            onPreviousPage={() => setPageIndex(index => Math.max(0, index - 1))}
            onNextPage={() =>
              setPageIndex(index => Math.min(pageCount - 1, index + 1))
            }
          />
        )}
      </div>

      <Dialog
        open={isModalOpen}
        onOpenChange={open => {
          if (!open) closeModal();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Edit license type' : 'Add license type'}
            </DialogTitle>
            <DialogDescription>
              This is the name staff see when recording a license.
            </DialogDescription>
          </DialogHeader>
          <form
            className='space-y-3'
            onSubmit={handleSubmit(data => void onSubmit(data))}
          >
            <div className='space-y-1'>
              <Input
                {...register('name')}
                placeholder='e.g. Fingerprint Clearance Card'
                aria-label='License type name'
                autoFocus
              />
              {errors.name && (
                <p className='text-sm text-destructive'>
                  {errors.name.message}
                </p>
              )}
              {submitError && (
                <p className='text-sm text-destructive'>{submitError}</p>
              )}
            </div>
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={closeModal}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                )}
                {editingId ? 'Save' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!pendingRetire}
        onOpenChange={open => {
          if (!open) setPendingRetire(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retire {pendingRetire?.name}?</DialogTitle>
            <DialogDescription>
              It stops being offered when recording a new license. Licenses
              already recorded against it keep it, and you can restore it later.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => setPendingRetire(null)}
              disabled={!!busyId}
            >
              Cancel
            </Button>
            <Button
              type='button'
              variant='destructive'
              onClick={() => void confirmRetire()}
              disabled={!!busyId}
            >
              {busyId && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
              Retire
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserLicenseTypesSection;
